import { Empty } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { SaveKnowledge, SupplementalKnowledgeAiReply } from 'services/api/supplementKnowledge';
import { T, useTranslate } from '@tolgee/react';
import emptySvg from 'views/Knowledge/assets/empty.svg';
import TransComponent from 'components/TransComponent';
import { eventBus } from 'services/eventBus';
import { KnowledgeList } from './KnowledgeList';
import { Container, Title } from './styles';
import { isPlatformProductCitation, transformKnowledge } from './utils';
import { PlatformProductNotice } from './PlatformProductNotice';

function CiteKnowledge({
  caseId,
  conversation,
  aiReply,
  applySkuIds,
  onKnowledgesUpdate,
  onKnowledgeSaved,
}: {
  caseId?: number;
  conversation: Pick<Conversation.Conversation, 'storeId' | 'region' | 'channel'>;
  aiReply: SupplementalKnowledgeAiReply;
  applySkuIds: string[];
  onKnowledgesUpdate?: (knowledegs: SaveKnowledge[][]) => void;
  onKnowledgeSaved?: () => void;
}) {
  const { replyTime, citeKnowledge, sourceKnowledge } = aiReply;

  const { t } = useTranslate();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const knowledgeList = useMemo(() => {
    return transformKnowledge(citeKnowledge, sourceKnowledge);
  }, [citeKnowledge, sourceKnowledge]);
  const hasPlatformProductCitation = citeKnowledge.some(isPlatformProductCitation);

  useEffect(() => {
    const handler = (scrollTop: number = 0) => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      }
    };

    eventBus.on('CITE_KNOWLEDGE_SCROLL', handler);

    return () => {
      eventBus.off('CITE_KNOWLEDGE_SCROLL', handler);
    };
  }, []);

  return (
    <Container ref={containerRef}>
      <Title>
        <T
          keyName="从知识库x找到的相关知识"
          params={{
            x: (
              <TransComponent
                content={<span>（{dayjs(replyTime).format('YYYY-MM-DD HH:mm:ss')}）</span>}
              />
            ),
          }}
        />
        （{knowledgeList.length + Number(hasPlatformProductCitation)}）
      </Title>
      {hasPlatformProductCitation && <PlatformProductNotice />}
      {knowledgeList.length === 0 && !hasPlatformProductCitation && (
        <Empty
          image={<img src={emptySvg} alt="empty" />}
          description={t('x_not_yet', { x: t('知识') })}
        />
      )}
      {knowledgeList.length > 0 && (
        <>
          <KnowledgeList
            caseId={caseId}
            conversation={conversation}
            knowledgeList={knowledgeList}
            applySkuIds={applySkuIds}
            onKnowledgesUpdate={onKnowledgesUpdate}
            onKnowledgeSaved={onKnowledgeSaved}
          />
        </>
      )}
    </Container>
  );
}

export default CiteKnowledge;
